import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, ArrowUpRight, X } from "lucide-react";
import { Container, CodeTag } from "@/components/Section";
import { Reveal, LineReveal } from "@/components/motion";
import Seo from "@/components/Seo";
import { fetchJobs, submitApplication } from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1509453721491-c3af5961df76?auto=format&fit=crop&w=2000&q=80";

function ApplyDialog({ job, onClose }) {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    location: "",
    years_experience: "",
    cover_letter: "",
  });
  const [submitting, setSubmitting] = useState(false);

  if (!job) return null;
  const handleChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone) {
      toast.error("Name, email and phone are required.");
      return;
    }
    setSubmitting(true);
    try {
      await submitApplication({ job_id: job.id, ...form });
      toast.success(`Application sent for ${job.title}. We'll be in touch.`);
      onClose();
    } catch {
      toast.error("Something went wrong. Try again or email info@aprasim.ca");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="apply-modal">
      <div className="w-full max-w-2xl bg-[#F0F0EC] brut-border brut-shadow max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b-2 border-black">
          <div>
            <div className="label-mono text-[#FF4F00]">{job.code} · Application</div>
            <h3 className="font-display text-2xl uppercase tracking-tight mt-1">{job.title}</h3>
          </div>
          <button onClick={onClose} data-testid="apply-modal-close" className="w-10 h-10 brut-border flex items-center justify-center hover:bg-black hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="apply-form">
          <Field label="Full Name *" testid="apply-name" value={form.full_name} onChange={handleChange("full_name")} />
          <Field label="Email *" testid="apply-email" type="email" value={form.email} onChange={handleChange("email")} />
          <Field label="Phone *" testid="apply-phone" value={form.phone} onChange={handleChange("phone")} />
          <Field label="City / Region" testid="apply-location" value={form.location} onChange={handleChange("location")} />
          <Field label="Years of Experience" testid="apply-experience" value={form.years_experience} onChange={handleChange("years_experience")} className="sm:col-span-2" />
          <div className="sm:col-span-2">
            <label className="label-mono text-neutral-700">Cover Note</label>
            <textarea
              data-testid="apply-cover-letter"
              value={form.cover_letter}
              onChange={handleChange("cover_letter")}
              rows={5}
              className="mt-2 w-full px-4 py-3 bg-white brut-border font-sans text-sm focus:outline-none focus:border-[#FF4F00] focus:ring-2 focus:ring-[#FF4F00]"
              placeholder="Tell us about your experience, equipment proficiency and availability…"
            />
          </div>
          <div className="sm:col-span-2 mt-2 flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-600">
              Resume upload coming soon — paste a link in cover note for now.
            </p>
            <button
              type="submit"
              disabled={submitting}
              data-testid="apply-submit"
              className="inline-flex items-center gap-3 bg-black text-white px-6 py-4 font-mono text-xs uppercase tracking-[0.25em] hover:bg-[#FF4F00] hover:text-black transition-colors disabled:opacity-60"
            >
              {submitting ? "Sending…" : "Submit Application"} <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, testid, value, onChange, type = "text", className = "" }) {
  return (
    <div className={className}>
      <label className="label-mono text-neutral-700">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={onChange}
        className="mt-2 w-full px-4 py-3 bg-white brut-border font-sans text-sm focus:outline-none focus:border-[#FF4F00] focus:ring-2 focus:ring-[#FF4F00]"
      />
    </div>
  );
}

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(null);
  const [applyFor, setApplyFor] = useState(null);

  useEffect(() => {
    fetchJobs().then(setJobs).catch(() => {});
  }, []);

  return (
    <div data-testid="careers-page">
      <Seo
        title="Careers | Civil Construction Jobs in Chilliwack, BC — APRASIM"
        description="APRASIM Contracting Corp. crew — heavy equipment operators, foremen, superintendents, estimators and HSE across the Fraser Valley. No open positions right now, but we keep resumes on file for future openings."
        path="/careers"
      />
      {/* HERO */}
      <section className="border-b-2 border-black">
        <Container className="py-16 sm:py-24 grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 lg:col-span-7">
            <Reveal y={20}>
              <CodeTag>CAREERS / CREW</CodeTag>
            </Reveal>
            <LineReveal
              className="mt-6 font-display text-5xl sm:text-7xl uppercase tracking-tighter leading-[0.92]"
              delay={0.15}
              lines={[<>Build your career</>, <>in <span className="text-[#FF4F00]">the dirt</span></>]}
            />
            <Reveal delay={0.4}>
              <p className="mt-8 text-lg text-neutral-700 leading-relaxed max-w-2xl">
                Operators, supers, PMs, estimators and HSE. We hire crew members who stay — and we promote from inside the gate.
              </p>
              <div className="mt-8 flex items-center gap-6 font-mono text-[12px] uppercase tracking-[0.25em] text-neutral-700">                <span className="flex items-center gap-2"><span className="w-2 h-2 bg-[#FF4F00]" /> No open positions right now</span>
                <span className="hidden sm:inline">·</span>
                <span>Benefits + Pension</span>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.25} className="col-span-12 lg:col-span-5 group overflow-hidden brut-border bg-black">
            <img src={HERO_IMG} alt="Hard hats" className="w-full h-[420px] object-cover img-gray img-zoom" />
          </Reveal>
        </Container>
      </section>

      {/* JOB LIST */}
      <section data-testid="job-list" className="border-b-2 border-black">
        <Container className="py-16">
          <div className="brut-border bg-[#F0F0EC]">
            {jobs.map((job, i) => {
              const isOpen = open === job.id;
              return (
                <div key={job.id} className={`${i < jobs.length - 1 ? "border-b-2 border-black" : ""}`}>
                  <button
                    onClick={() => setOpen(isOpen ? null : job.id)}
                    data-testid={`job-toggle-${job.slug}`}
                    className="w-full p-6 sm:p-8 grid grid-cols-12 gap-4 items-center text-left hover:bg-[#E5E5DF] transition-colors"
                  >
                    <div className="col-span-12 sm:col-span-2 font-mono text-[11px] tracking-[0.25em] uppercase text-neutral-600">
                      {job.code}
                    </div>
                    <div className="col-span-12 sm:col-span-5">
                      <h3 className="font-display text-2xl uppercase tracking-tight">{job.title}</h3>
                      <p className="text-sm text-neutral-700 mt-1">{job.department}</p>
                    </div>
                    <div className="col-span-6 sm:col-span-2 flex flex-wrap gap-2">
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 bg-[#FF4F00] text-black">
                        {job.employment_type}
                      </span>
                    </div>
                    <div className="col-span-6 sm:col-span-2 font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-700">
                      {job.location}
                    </div>
                    <div className="col-span-12 sm:col-span-1 flex justify-end">
                      {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </div>
                  </button>
                  {isOpen && (
                    <div data-testid={`job-detail-${job.slug}`} className="p-6 sm:p-10 bg-white border-t-2 border-black grid grid-cols-12 gap-6">
                      <div className="col-span-12 lg:col-span-7">
                        <p className="text-lg text-neutral-800 leading-relaxed">{job.summary}</p>
                        <div className="mt-8">
                          <div className="label-mono text-[#FF4F00]">Responsibilities</div>
                          <ul className="mt-4 space-y-2">
                            {job.responsibilities.map((r) => (
                              <li key={r} className="flex items-start gap-3 text-sm">
                                <span className="mt-2 w-1.5 h-1.5 bg-black flex-shrink-0" />
                                <span>{r}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="mt-8">
                          <div className="label-mono text-[#FF4F00]">Requirements</div>
                          <ul className="mt-4 space-y-2">
                            {job.requirements.map((r) => (
                              <li key={r} className="flex items-start gap-3 text-sm">
                                <span className="mt-2 w-1.5 h-1.5 bg-black flex-shrink-0" />
                                <span>{r}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      <div className="col-span-12 lg:col-span-5">
                        <div className="brut-border p-6 bg-[#E5E5DF]">
                          <div className="label-mono">Compensation</div>
                          <div className="font-display text-2xl uppercase mt-2">{job.salary_range || "Competitive"}</div>
                          <div className="mt-6 label-mono">Benefits</div>
                          <ul className="mt-3 space-y-2 font-mono text-xs tracking-wider text-neutral-700">
                            <li>// Extended health & dental</li>
                            <li>// Pension matching</li>
                            <li>// LOA & travel coverage</li>
                            <li>// Boot & PPE allowance</li>
                          </ul>
                          <button
                            onClick={() => setApplyFor(job)}
                            data-testid={`apply-btn-${job.slug}`}
                            className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-black text-white px-6 py-4 font-mono text-xs uppercase tracking-[0.25em] hover:bg-[#FF4F00] hover:text-black transition-colors"
                          >
                            Apply Now <ArrowUpRight className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {jobs.length === 0 && (
              <div data-testid="no-openings-notice" className="p-10 sm:p-14 text-center">
                <div className="label-mono text-[#FF4F00]">Status / Hiring</div>
                <h3 className="mt-4 font-display text-3xl sm:text-4xl uppercase tracking-tight">
                  We&apos;re not hiring right now
                </h3>
                <p className="mt-4 text-neutral-700 leading-relaxed max-w-xl mx-auto">
                  There are no open positions at the moment. We keep every resume on file — when the next job breaks ground, we hire from this list first.
                </p>
                <a
                  href="mailto:info@aprasim.ca?subject=Resume%20on%20file%20—%20APRASIM%20Careers"
                  data-testid="careers-resume-email-link"
                  className="mt-8 inline-flex items-center gap-3 bg-black text-white px-6 py-4 font-mono text-xs uppercase tracking-[0.25em] hover:bg-[#FF4F00] hover:text-black transition-colors duration-300"
                >
                  Send Us Your Resume <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
            )}
          </div>
        </Container>
      </section>

      <ApplyDialog job={applyFor} onClose={() => setApplyFor(null)} />
    </div>
  );
}
